import { http } from "../api/http";
import { NoteBody } from "../views/workspace/NoteBody";
import { NoteComposer } from "./references/NoteComposer";
import { useCallback, useEffect, useRef, useState } from "react";
import { create } from "zustand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, GripHorizontal } from "lucide-react";
import { notesApi, type Note } from "../api/workspace-notes.api";
import { useAuthStore } from "../store/auth.store";
import "./StickyNotes.css";

export type Pinned = {
  id: string;
  scope: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
};
export const usePins = create<{
  items: Pinned[];
  pin: (id: string, scope: string) => void;
  close: (id: string) => void;
  move: (id: string, x: number, y: number) => void;
  clear: () => void;
}>((set) => ({
  items: [],
  pin: (id, scope) =>
    set((s) =>
      s.items.length >= 20 ||
      s.items.some((p) => p.id === id && p.scope === scope)
        ? s
        : {
            items: [
              ...s.items,
              {
                id,
                scope,
                x: 40 + (s.items.length % 6) * 34,
                y: 100 + (s.items.length % 6) * 30,
              },
            ],
          },
    ),
  close: (id) => set((s) => ({ items: s.items.filter((p) => p.id !== id) })),
  move: (id, x, y) =>
    set((s) => ({
      items: s.items.map((p) => (p.id === id ? { ...p, x, y } : p)),
    })),
  clear: () => set({ items: [] }),
}));
const pinKey = (scope: string) => "permio:pinned-notes:v1:" + scope;
let pinScope = "";
let restoringPins = false;
usePins.subscribe((state) => {
  if (!pinScope || restoringPins) return;
  try {
    localStorage.setItem(
      pinKey(pinScope),
      JSON.stringify(state.items.filter((p) => p.scope === pinScope)),
    );
  } catch {
    /* Storage may be disabled. */
  }
});
function restorePins() {
  const auth = useAuthStore.getState();
  const scope =
    auth.user?.id && auth.activeSchoolId
      ? auth.user.id + ":" + auth.activeSchoolId
      : "";
  if (scope === pinScope) return;
  pinScope = scope;
  restoringPins = true;
  let items: Pinned[] = [];
  try {
    const data: unknown = scope
      ? JSON.parse(localStorage.getItem(pinKey(scope)) || "[]")
      : [];
    if (Array.isArray(data))
      items = data
        .filter(
          (p) =>
            p &&
            p.scope === scope &&
            typeof p.id === "string" &&
            Number.isFinite(p.x) &&
            Number.isFinite(p.y),
        )
        .slice(0, 20)
        .map((p) => ({
          id: p.id,
          scope,
          x: p.x,
          y: p.y,
          width: Number.isFinite(p.width) ? p.width : undefined,
          height: Number.isFinite(p.height) ? p.height : undefined,
        }));
  } catch {
    /* Invalid preferences are ignored. */
  }
  usePins.setState({ items });
  restoringPins = false;
}
useAuthStore.subscribe(restorePins);
restorePins();
// Merge only local changes, so independent edits on another device survive.
export function mergePins(
  remote: Pinned[],
  changes: Map<string, Pinned | null>,
  localOrder: readonly string[] = [],
) {
  const map = new Map(remote.map((p) => [p.id, p]));
  for (const [id, pin] of changes) {
    if (pin) map.set(id, pin);
    else map.delete(id);
  }
  // La réponse serveur actualise les préférences, pas le premier plan choisi
  // dans cet onglet. Les nouvelles notes distantes arrivent au-dessus.
  const ordered: Pinned[] = [];
  for (const id of localOrder) {
    const pin = map.get(id);
    if (pin) {
      ordered.push(pin);
      map.delete(id);
    }
  }
  return [...ordered, ...map.values()];
}
function usePinSync(scope: string) {
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (!scope) return;
    let alive = true,
      busy = false,
      applying = false,
      previous = usePins.getState().items;
    const outboxKey = pinKey(scope) + ":pending";
    const changes = new Map<string, Pinned | null>();
    try {
      for (const [id, pin] of JSON.parse(
        localStorage.getItem(outboxKey) || "[]",
      ))
        if (typeof id === "string" && (!pin || pin.scope === scope))
          changes.set(id, pin);
    } catch {
      /* Invalid outbox ignored. */
    }
    const persist = () => {
      try {
        localStorage.setItem(outboxKey, JSON.stringify([...changes]));
      } catch {
        /* unavailable */
      }
    };
    const unsubscribe = usePins.subscribe((state) => {
      if (pinScope !== scope) return;
      if (applying) {
        previous = state.items;
        return;
      }
      const before = previous.filter((p) => p.scope === scope),
        after = state.items.filter((p) => p.scope === scope);
      for (const p of after)
        if (
          JSON.stringify(p) !==
          JSON.stringify(before.find((x) => x.id === p.id))
        )
          changes.set(p.id, p);
      for (const p of before)
        if (!after.some((x) => x.id === p.id)) changes.set(p.id, null);
      previous = state.items;
      persist();
      setStatus("Synchronisation…");
    });
    const current = () => alive && pinScope === scope;
    let nextRead = 0;
    let retryAt = 0;
    const sync = async () => {
      if (busy || !current() || document.hidden || !navigator.onLine) return;
      if (Date.now() < retryAt || (!changes.size && Date.now() < nextRead)) return;
      busy = true;
      try {
        const { data } = await http.get<{ version: number; items: Pinned[] }>(
          "/v1/workspace/note-pins",
        );
        if (!current()) return;
        if (!Number.isInteger(data.version) || !Array.isArray(data.items))
          throw Error("Réponse invalide");
        if (data.version === 0 && changes.size === 0)
          for (const pin of usePins
            .getState()
            .items.filter((p) => p.scope === scope))
            changes.set(pin.id, pin);
        let remote = data.items.map((p) => ({ ...p, scope }));
        if (changes.size) {
          const batch = new Map(changes);
          const merged = mergePins(remote, batch);
          if (merged.length > 20) throw Error("Limite de vingt post-it");
          const items = merged.map(({ id, x, y, width, height }) => ({
            id,
            x: Math.round(Math.max(0, x)),
            y: Math.round(Math.max(0, y)),
            ...(width ? { width: Math.round(width) } : {}),
            ...(height ? { height: Math.round(height) } : {}),
          }));
          await http.put("/v1/workspace/note-pins", {
            version: data.version,
            items,
          });
          if (!current()) return;
          for (const [id, pin] of batch)
            if (JSON.stringify(changes.get(id)) === JSON.stringify(pin))
              changes.delete(id);
          persist();
          remote = merged;
        }
        applying = true;
        const localOrder = usePins.getState().items
          .filter((pin) => pin.scope === scope)
          .map((pin) => pin.id);
        usePins.setState({ items: mergePins(remote, changes, localOrder) });
        applying = false;
        setStatus(changes.size ? "Synchronisation…" : "");
        nextRead = Date.now() + 30000;
        retryAt = 0;
      } catch (error: any) {
        const wait = Number(error.response?.data?.retryAfter ?? error.response?.headers?.['retry-after']);
        retryAt = Date.now() + (Number.isFinite(wait) && wait > 0 ? wait * 1000 : 30000);
        if (current())
          setStatus("Post-it conservés ici. Synchronisation en attente.");
      } finally {
        busy = false;
      }
    };
    void sync();
    const timer = setInterval(() => void sync(), 2000);
    const resume = () => void sync();
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      alive = false;
      unsubscribe();
      clearInterval(timer);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [scope]);
  return status;
}
function useScope() {
  const user = useAuthStore((s) => s.user?.id);
  const school = useAuthStore((s) => s.activeSchoolId);
  return user && school ? `${user}:${school}` : "";
}
function Sticky({
  pin,
  index,
  onNavigate,
}: {
  pin: Pinned;
  index: number;
  onNavigate: (href: string) => void;
}) {
  const school = useAuthStore((s) => s.activeSchoolId)!;
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ["sticky-note", pin.scope, pin.id],
    queryFn: () => notesApi.get(pin.id),
    refetchInterval: 15000,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
  const [draft, setDraft] = useState<Note | null>(null);
  const command = useRef({ payload: "", id: crypto.randomUUID() });
  const drag = useRef<{
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);
  const sizing = useRef<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const resizeNote = (width: number, height: number) => {
    usePins.setState((state) => ({
      items: state.items.map((item) =>
        item.id === pin.id
          ? {
              ...item,
              width: Math.min(
                Math.max(220, width),
                window.innerWidth - 16,
              ),
              height: Math.min(
                Math.max(180, height),
                window.innerHeight - 16,
              ),
            }
          : item,
      ),
    }));
  };
  const move = useCallback(
    (x: number, y: number) =>
      usePins
        .getState()
        .move(
          pin.id,
          Math.max(
            0,
            Math.min(
              x,
              window.innerWidth -
                Math.min(pin.width ?? 300, window.innerWidth - 16),
            ),
          ),
          Math.max(
            0,
            Math.min(
              y,
              window.innerHeight -
                Math.min(pin.height ?? 320, window.innerHeight - 16),
            ),
          ),
        ),
    [pin.id, pin.width, pin.height],
  );
  useEffect(() => {
    const resize = () => move(pin.x, pin.y);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // The current position is clamped whenever the viewport or position changes.
  }, [pin.x, pin.y, move]);
  const save = useMutation({
    mutationFn: async () => {
      if (!draft) return;
      const payload = {
        title: draft.title,
        body: draft.body,
        visibility: draft.visibility,
        memberIds: draft.memberIds,
        references: draft.references ?? [],
        mentionIds: draft.mentionIds ?? [],
        fileIds: draft.fileIds ?? [],
        version: draft.version,
      };
      const serialized = JSON.stringify(payload);
      if (command.current.payload !== serialized)
        command.current = { payload: serialized, id: crypto.randomUUID() };
      return notesApi.update(pin.id, {
        ...payload,
        requestId: command.current.id,
      });
    },
    onSuccess: async () => {
      setDraft(null);
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["sticky-note", pin.scope, pin.id],
        }),
        client.invalidateQueries({ queryKey: ["workspace-notes", school] }),
        client.invalidateQueries({ queryKey: ["workspace-note", school] }),
      ]);
    },
  });
  const allowed = !query.isError && query.data;
  return (
    <aside
      className="sticky-note"
      onPointerDownCapture={() => {
        const state = usePins.getState();
        if (state.items[state.items.length - 1]?.id !== pin.id)
          usePins.setState({
            items: [...state.items.filter((p) => p.id !== pin.id), pin],
          });
      }}
      aria-label={
        allowed ? `Post-it : ${query.data.title}` : "Post-it indisponible"
      }
      style={{
        left: Math.max(
          8,
          Math.min(
            pin.x,
            window.innerWidth -
              Math.min(pin.width ?? 300, window.innerWidth - 16) -
              8,
          ),
        ),
        top: Math.max(
          8,
          Math.min(
            pin.y,
            window.innerHeight -
              Math.min(pin.height ?? 320, window.innerHeight - 16) -
              8,
          ),
        ),
        width: Math.min(pin.width ?? 300, window.innerWidth - 16),
        height: Math.min(pin.height ?? 320, window.innerHeight - 16),
        zIndex: 1100 + index,
      }}
    >
      <header className="sticky-note-bar">
        <button
          type="button"
          className="sticky-grip"
          aria-label="Déplacer la note"
          title="Glisser pour déplacer · flèches au clavier"
          onPointerDown={(e) => {
            drag.current = {
              x: e.clientX,
              y: e.clientY,
              left: pin.x,
              top: pin.y,
            };
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (drag.current)
              move(
                drag.current.left + e.clientX - drag.current.x,
                drag.current.top + e.clientY - drag.current.y,
              );
          }}
          onPointerUp={() => {
            drag.current = null;
          }}
          onPointerCancel={() => {
            drag.current = null;
          }}
          onKeyDown={(e) => {
            const delta: Record<string, [number, number]> = {
              ArrowLeft: [-20, 0],
              ArrowRight: [20, 0],
              ArrowUp: [0, -20],
              ArrowDown: [0, 20],
            };
            if (delta[e.key]) {
              e.preventDefault();
              move(pin.x + delta[e.key][0], pin.y + delta[e.key][1]);
            }
          }}
        >
          <GripHorizontal size={20} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label="Fermer le post-it"
          title="Fermer sans supprimer la note"
          disabled={save.isPending}
          onClick={() => {
            if (
              !draft ||
              window.confirm("Fermer sans enregistrer les modifications ?")
            )
              usePins.getState().close(pin.id);
          }}
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <div className="sticky-note-body">
        {query.isError ? (
          <p role="alert">
            Note inaccessible pour le moment.{" "}
            <button onClick={() => void query.refetch()}>Réessayer</button>
          </p>
        ) : !allowed ? (
          <p>Chargement…</p>
        ) : draft ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!save.isPending) save.mutate();
            }}
          >
            <label>
              Titre
              <input
                aria-label="Titre du post-it"
                maxLength={180}
                required
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              />
            </label>
            <NoteComposer
              draft={draft}
              label="Contenu du post-it"
              disabled={save.isPending}
              onChange={(next) => setDraft({ ...draft, ...next })}
            />
            {save.isError && (
              <p role="alert">
                Enregistrement impossible. Vos modifications sont conservées ici
                ; en cas de conflit, annulez puis rouvrez la version actuelle.
              </p>
            )}
            <footer>
              <button
                disabled={save.isPending || !query.data.canEdit}
                type="submit"
              >
                Enregistrer
              </button>
              <button
                disabled={save.isPending}
                type="button"
                onClick={() => {
                  setDraft(null);
                  save.reset();
                }}
              >
                Annuler
              </button>
            </footer>
          </form>
        ) : (
          <div
            className="sticky-content"
            role="button"
            tabIndex={0}
            aria-disabled={!query.data.canEdit}
            onKeyDown={(e) => {
              if (
                query.data.canEdit &&
                e.target === e.currentTarget &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                setDraft({ ...query.data });
              }
            }}
            title={
              query.data.canEdit ? "Cliquer pour modifier" : "Lecture seule"
            }
            onClick={(e) => {
              if (!query.data.canEdit || (e.target as HTMLElement).closest("a"))
                return;
              setDraft({ ...query.data });
              save.reset();
            }}
          >
            <strong>{query.data.title}</strong>
            <NoteBody
              body={query.data.body || "Cliquez pour écrire…"}
              references={query.data.references}
              onNavigate={onNavigate}
              showDetails
            />
            <small>
              {query.data.canEdit ? "Cliquer pour modifier" : "Lecture seule"}
            </small>
          </div>
        )}
      </div>
      <button
        className="sticky-resize"
        type="button"
        aria-label="Redimensionner le post-it"
        title="Glisser pour redimensionner · flèches au clavier"
        onPointerDown={(e) => {
          const box = e.currentTarget.parentElement!.getBoundingClientRect();
          sizing.current = {
            x: e.clientX,
            y: e.clientY,
            width: box.width,
            height: box.height,
          };
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (sizing.current)
            resizeNote(
              sizing.current.width + e.clientX - sizing.current.x,
              sizing.current.height + e.clientY - sizing.current.y,
            );
        }}
        onPointerUp={() => {
          sizing.current = null;
        }}
        onPointerCancel={() => {
          sizing.current = null;
        }}
        onKeyDown={(e) => {
          const delta: Record<string, [number, number]> = {
            ArrowLeft: [-20, 0],
            ArrowRight: [20, 0],
            ArrowUp: [0, -20],
            ArrowDown: [0, 20],
          };
          if (delta[e.key]) {
            e.preventDefault();
            resizeNote(
              (pin.width ?? 300) + delta[e.key][0],
              (pin.height ?? 320) + delta[e.key][1],
            );
          }
        }}
      >
        ◢
      </button>
    </aside>
  );
}
export function StickyNotes({
  onNavigate,
}: {
  onNavigate: (href: string) => void;
}) {
  const scope = useScope();
  const pins = usePins((s) => s.items);
  const syncStatus = usePinSync(scope);

  return (
    <>
      {syncStatus && (
        <div className="sticky-sync-status" role="status">
          {syncStatus}
        </div>
      )}
      {pins
        .filter((p) => p.scope === scope)
        // Garder les nœuds en place pendant le pointerdown qui change le z-index.
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((pin) => (
          <Sticky
            key={pin.scope + pin.id}
            pin={pin}
            index={pins.indexOf(pin)}
            onNavigate={onNavigate}
          />
        ))}
    </>
  );
}
